import React, { useEffect, useRef, useState } from 'react';
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from 'src/componentes/dropdown';
import { Row, Col } from 'src/componentes/grid';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';

import Button from '../button';
import Dialog from '../dialog';
import LoadingContainer from '../loading-container';
import http from 'src/services/httpService';
import httpSelector from 'src/services/httpSelector';
import Alert from '../message-box/alert';
import ContentManager from '../content-manager';
import Confirm from '../message-box/confirm';
import errorHandler from '../../utils/error-handler';
import Email from '../email';
import { DownloadFile, ParseError } from 'src/services/downloadFileUrl';
import MessageCancelEdit from './message-cancel-edit';
import Tooltip from 'src/componentes/tooltip';
import { SophieIcon } from 'src/componentes/icons';
import useAuth from 'src/hooks/useAuth';
import useFormAdapter from 'src/utils/useFormAdapter';

function EditModal({
  showActions = true,
  showDelete = false,
  showExport = false,
  showSave = true,
  saveLabelId = 'label.salvar',
  saveLabelValues = null,
  showExportExcel = true,
  showExportPDF = true,
  urlExport = '',
  refreshModel = null,
  asposeToPdf = true,
  usePuppeteerPDF = false,
  refreshOnDelete = true,
  reloadAfterDelete = true,
  validateAlterModel = true,
  showEmailSubject = false,
  enviarComunicado = false,
  showErrorsCustom = null,
  requiredMessage = 'label.osCamposDestacadosEmVermelhoSaoObrigatoriosFavorPreenchelos',
  ...receivedProps
}) {
  const intl = useIntl();
  const { terms: resources, moduloInteligenciaArtificial } = useAuth();
  const viewType = useSelector((state) => state.viewType);
  const props = {
    ...receivedProps,
    showActions,
    showDelete,
    showExport,
    showSave,
    saveLabelId,
    saveLabelValues,
    showExportExcel,
    showExportPDF,
    urlExport,
    refreshModel,
    asposeToPdf,
    usePuppeteerPDF,
    refreshOnDelete,
    reloadAfterDelete,
    validateAlterModel,
    showEmailSubject,
    enviarComunicado,
    showErrorsCustom,
    requiredMessage,
    intl,
    resources,
    viewType,
    moduloInteligenciaArtificial,
  };
  const form = useFormAdapter(props.schema);
  const dialogRef = useRef(null);
  const salvoRef = useRef(false);
  const salvandoRef = useRef(false);
  const [isLoading, setIsLoading] = useState(false);
  const [id, setId] = useState(props.item ? props.item.id : null);
  const [modelInicial, setModelInicial] = useState(null);
  const [showErrorRequired, setShowErrorRequired] = useState(false);
  const [checkRequiredCustom, setCheckRequiredCustom] = useState(0);

  function refresh() {
    setIsLoading(true);

    if (props.local && props.item && props.item.id <= 0) {
      const initialModel = props.item.editModel
        ? props.item.editModel
        : props.item;

      setTimeout(() => {
        form.setValue(null, initialModel);
      }, 250);

      setModelInicial({ ...initialModel });
      setIsLoading(false);
    } else if (
      props.url &&
      (!props.item || props.item.id === -1 || props.isNew)
    ) {
      http
        .post(`${props.url}/Create`, {
          parameters: props.parameters,
        })
        .then((response) => {
          form.setValue(null, response.data);
          setModelInicial({ ...response.data });
          setIsLoading(false);
        })
        .catch((error) => {
          setIsLoading(false);
          errorHandler(error);
          handleClose();
        });
    } else if (props.url) {
      httpSelector
        .getHttp(props.viewType)
        .post(`${props.url}/Edit`, {
          id: props.item.id,
          parameters: props.parameters,
        })
        .then((response) => {
          form.setValue(null, response.data);
          setModelInicial({ ...response.data });
          setIsLoading(false);
        })
        .catch((error) => {
          setIsLoading(false);
          errorHandler(error);
          handleClose();
        });
    } else {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, [props.refreshId]);

  function showErrors() {
    if (props.showErrorsCustom) {
      const errorCustom = props.showErrorsCustom(form.getValue());
      if (errorCustom) {
        if (props.enableShowAllErrors) form.showAllErrors();

        setShowErrorRequired(true);
        setCheckRequiredCustom((value) => value + 1);

        return false;
      }
    }

    const isValid = form.isValid();

    if (!isValid) {
      props.handleErrorRequired &&
        form.handleErrorRequired &&
        form.handleErrorRequired();
      form.showAllErrors();
      setShowErrorRequired(true);
      setCheckRequiredCustom((value) => value + 1);
      ContentManager.addContent(
        <Alert message={<FormattedMessage id={props.requiredMessage} />} />
      );
      return false;
    }

    return true;
  }

  function handleSaveBeforeSave(fechar, camposAdicionais) {
    if (props.handleConfirmeBeforeSave)
      props.handleConfirmeBeforeSave(
        form.getValue(),
        (fecharAposSalvar) => handleSave(fecharAposSalvar, camposAdicionais),
        props.intl,
        fechar,
        props.resources,
        modelInicial,
        showErrors,
        props.afterSave
      );
    else handleSave(fechar, camposAdicionais);
  }

  function handleSave(fechar, camposAdicionais) {
    if (salvandoRef.current) return;

    const { ignoreValidation = false } = props;
    const model = form.getValue();
    const isNew = model.id <= 0;

    if (!ignoreValidation) {
      const isValid = showErrors();
      if (!isValid) return;
    }

    salvandoRef.current = true;
    setIsLoading(true);

    if (props.handleSave) {
      salvoRef.current = true;
      Promise.resolve()
        .then(() => props.handleSave(model, handleClose, fechar))
        .then((savedModel) => {
          if (savedModel && typeof savedModel === 'object') {
            const nextModel = {
              ...model,
              ...savedModel,
            };

            setId(nextModel.id);
            setModelInicial({ ...nextModel });
            form.setValue(null, nextModel);
          }
        })
        .catch((error) => {
          errorHandler(error);
        })
        .finally(() => {
          salvandoRef.current = false;
          setIsLoading(false);
        });
    } else {
      http
        .post(`${props.url}/Save`, {
          model: camposAdicionais ? { ...model, ...camposAdicionais } : model,
          parameters: props.parameters,
        })
        .then((response) => {
          if (props.sendMailSave) {
            const model = form.getModel();
            let htmlString = '';
            let width = 0;
            let height = 0;

            if (props.handleHtmlString) {
              const relatorio = props.handleHtmlString(model);

              if (relatorio) {
                htmlString = relatorio.htmlString;
                width = relatorio.width;
                height = relatorio.height;
              }
            }

            props.sendMailSave(htmlString, width, height);
          }

          salvoRef.current = true;
          setId(response.data.model.id);
          setModelInicial({ ...response.data.model });
          form.setValue(null, response.data.model);

          props.handleCallBack && props.handleCallBack();
          if (fechar) {
            handleClose();
          }
          props.refreshModel != null && props.refreshModel();
          props.afterSave != null && props.afterSave(form.getValue(), isNew);

          if (!fechar && props.messageSave) {
            ContentManager.addContent(
              <Alert
                title={props.intl.formatMessage({ id: 'sucesso' })}
                message={props.intl.formatMessage({
                  id: props.messageSave,
                })}
              />
            );
          }
        })
        .catch((error) => {
          setCheckRequiredCustom((value) => value + 1);
          errorHandler(error);
        })
        .finally(() => {
          salvandoRef.current = false;
          setIsLoading(false);
        });
    }
  }

  function apenasFotoAlterada() {
    const initialModelWithoutPhoto = { ...modelInicial };
    const currentModelWithoutPhoto = { ...form.getValue() };
    delete initialModelWithoutPhoto.fotoArquivo;
    delete currentModelWithoutPhoto.fotoArquivo;

    return (
      JSON.stringify(initialModelWithoutPhoto) ===
      JSON.stringify(currentModelWithoutPhoto)
    );
  }

  function confirmClose() {
    const modelAtual = form.getValue();

    if (
      validateAlterModel &&
      ((JSON.stringify(modelInicial) !== JSON.stringify(modelAtual) &&
        !apenasFotoAlterada()) ||
        props.formComponentRef?.current?.hasUnsavedChanges?.())
    ) {
      ContentManager.addContent(
        <MessageCancelEdit
          handleNaoSalvar={() => handleClose()}
          handleSave={handleSaveBeforeSave}
        />
      );
    } else {
      handleClose();
    }
  }

  function handleCloseDialog(deleted = false, closeModalChild = false) {
    document.body.classList.remove('modal-open');
    dialogRef.current?.close();

    setTimeout(() => {
      props.remove?.();
      props.handleOnClose?.();
      props.handleClose?.(
        salvoRef.current || props.forceRefresh,
        form.getValue('id'),
        form.getValue(),
        deleted,
        closeModalChild
      );
    }, 300);
  }

  function handleClose(deleted = false, closeModalChild = false) {
    if (props.handleConfirmeBeforeClose)
      props.handleConfirmeBeforeClose(
        form.getValue(),
        handleCloseDialog,
        deleted,
        closeModalChild
      );
    else handleCloseDialog(deleted, closeModalChild);
  }

  function handleDeleteClick(item, index) {
    if (props.onBeforeDelete) {
      props.onBeforeDelete(item, {
        onDeleted: () => handleClose(true),
      });
      return;
    }

    if (props.onDeleting) {
      props.onDeleting(item, index);
      dialogRef.current?.close();
    } else {
      ContentManager.addContent(
        <Confirm
          message={props.intl.formatMessage({
            id: 'label.desejaMesmoExcluirEsseRegistro',
          })}
          handleConfirm={() => handleDeleteClickConfirm(item, index)}
        />
      );
    }
  }

  function handleDeleteClickConfirm(item, index) {
    if (props.local) {
      props.handleRemove?.(item, index);
      handleClose(true);
    } else {
      setIsLoading(true);

      http
        .post(`${props.url}${props.endpointRemove ?? '/Remove'}`, {
          id: item ? item.id : id,
          parameters: props.parameters,
        })
        .then(() => {
          ContentManager.addContent(
            <Alert
              title={props.intl.formatMessage({ id: 'sucesso' })}
              message={props.intl.formatMessage({
                id: 'label.registroExcluidoComSucesso',
              })}
              deleted={props.refreshOnDelete}
              reloadAfterDelete={props.reloadAfterDelete}
            />
          );
          handleClose(true);
        })
        .catch((error) => {
          errorHandler(error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }

  function handleEmailClick(item, extensaoExport) {
    const { htmlString, width, height } = getExportContent();

    ContentManager.addContent(
      <Email
        url={props.url}
        urlExport={props.urlExport}
        showSubject={props.showEmailSubject}
        item={item}
        extensaoExport={extensaoExport}
        parameters={props.parameters}
        htmlString={htmlString}
        widthExport={width}
        heightExport={height}
      />
    );
  }

  function getExportContent() {
    let htmlString = '';
    let width = 0;
    let height = 0;

    if (props.handleHtmlString) {
      const relatorio = props.handleHtmlString(form.getModel());

      if (relatorio) {
        htmlString = relatorio.htmlString;
        width = relatorio.width;
        height = relatorio.height;
      }
    } else {
      htmlString = document.getElementById('relatorioPag')?.outerHTML || '';
    }

    return { htmlString, width, height };
  }

  function handleExportClick(item, extensaoExport) {
    const { htmlString, width, height } = getExportContent();

    // TODO: este codigo deve ser feito dentro de cada tela que chama o edit-dialog
    // remover este codigo e colocar dentro da tela que chamou o edit-dialog
    handleSaveBeforeSave(false);

    if (props.urlExport) {
      setIsLoading(true);
      ContentManager.addContent(
        <Alert message={props.intl.formatMessage({ id: 'downloadEmBreve' })} />
      );
      http
        .post(
          props.urlExport,
          {
            id: item.id,
            extensaoExport,
            parameters: props.parameters,
            htmlString,
            width,
            height,
          },
          {
            responseType: 'arraybuffer',
          }
        )
        .then((response) => {
          setIsLoading(false);

          DownloadFile(response);
        })
        .catch((error) => {
          setIsLoading(false);

          errorHandler(ParseError(error));
        });
    }
  }

  function handleExportPuppeteerPDF(item, parameters, extensaoExport) {
    handleSaveBeforeSave(false);
    if (props.usePuppeteerPDF && props.divIdToExport) {
      setIsLoading(true);
      ContentManager.addContent(
        <Alert message={props.intl.formatMessage({ id: 'downloadEmBreve' })} />
      );
      http
        .post(
          props.urlExportPuppeteerPDF,
          {
            relatorioId: item.id,
            fileExtension: extensaoExport,
            orientacaoId: 1,
            parameters,
          },
          {
            responseType: 'arraybuffer',
          }
        )
        .then((response) => {
          setIsLoading(false);

          DownloadFile(response);

          dialogRef.current?.close();
        })
        .catch((error) => {
          setIsLoading(false);
          errorHandler(ParseError(error));
        });
    }
  }
  const { HeaderCustomComponent, ehReuniao } = props;
  const model = form.getValue();
  const isNew = model.id <= 0;
  const permissionsModel = form.getModel('permissions');
  const allowDelete = permissionsModel.getModel('allowDelete').value;
  const allowEdit =
    permissionsModel.getModel('allowEdit').value ||
    permissionsModel.getModel('allowEditFallback').value;

  let footerButton = props.showActions && props.showExport && id && (
    <UncontrolledDropdown group key="menu-exportar" className="mr-2 d-inline">
      <DropdownToggle toolbar icon="dots-horizontal" />
      <DropdownMenu>
        <DropdownItem onClick={() => handleEmailClick(props.item, 'xlsx')}>
          <FormattedMessage id="enviarEmail" />
        </DropdownItem>
        {props.asposeToPdf && props.showExportPDF && (
          <DropdownItem onClick={() => handleExportClick(props.item, 'pdf')}>
            <FormattedMessage id="label.exportarPDF" />
          </DropdownItem>
        )}
        {props.showExportExcel && (
          <DropdownItem onClick={() => handleExportClick(props.item, 'xlsx')}>
            <FormattedMessage id="label.exportarExcel" />
          </DropdownItem>
        )}
        {props.usePuppeteerPDF && (
          <DropdownItem
            onClick={() =>
              handleExportPuppeteerPDF(props.item, props.parameters, 'pdf')
            }
          >
            <FormattedMessage id="label.exportarPDF" />
          </DropdownItem>
        )}
      </DropdownMenu>
    </UncontrolledDropdown>
  );

  if (
    props.showDeleteInFooter &&
    props.showActions &&
    props.showDelete &&
    allowDelete &&
    id
  ) {
    const deleteInFooter = props.showIconDelete ? (
      <Button
        data-testid="edit-dialog-delete-button"
        color="danger"
        outline
        toolbar
        icon="Trash2"
        onClick={() => handleDeleteClick(props.item, props.item)}
      />
    ) : (
      <Button
        className="mr-2"
        color="danger"
        onClick={() => handleDeleteClick(props.item, props.item)}
      >
        <FormattedMessage id="excluir" />
      </Button>
    );

    footerButton = footerButton ? (
      <div className="d-flex align-items-center flex-wrap">
        {footerButton}
        {deleteInFooter}
      </div>
    ) : (
      deleteInFooter
    );
  }

  const editActions = props.showActions
    ? [
        <div key="edit-toolbar" className={ehReuniao ? 'reuniao-step7' : ''}>
          <div className="botoes-padrao">
            {!props.showIconDelete && (
              <Button
                className={props.classCancelButton}
                color={props.colorCancelButton || 'secondary'}
                onClick={() =>
                  props.confirmOnCancel ? confirmClose() : handleClose()
                }
              >
                <FormattedMessage id="label.cancelar" />
              </Button>
            )}
            {!props.showIconDelete && id && props.actions}
            {!props.showDeleteInFooter &&
              !props.showIconDelete &&
              props.showActions &&
              props.showDelete &&
              allowDelete &&
              id && (
                <Button
                  className="ml-2"
                  color="danger"
                  onClick={() => handleDeleteClick(props.item, props.item)}
                >
                  <FormattedMessage id="excluir" />
                </Button>
              )}
            {!props.showDeleteInFooter &&
              props.showIconDelete &&
              props.showActions &&
              props.showDelete &&
              allowDelete &&
              id && (
                <Button
                  data-testid="edit-dialog-delete-button"
                  className="tw:ms-2"
                  color="danger"
                  outline
                  toolbar
                  icon="Trash2"
                  onClick={() => handleDeleteClick(props.item, props.item)}
                />
              )}
            {props.showIconDelete && (
              <Button
                className="tw:ms-2"
                color="outline-primary"
                onClick={() =>
                  props.confirmOnCancel ? confirmClose() : handleClose()
                }
              >
                <FormattedMessage id="label.cancelar" />
              </Button>
            )}
            {!props.disabledEdit &&
              (props.forceShowSave ||
                (props.showActions && props.showSave && allowEdit)) && (
                <Button
                  data-testid="edit-dialog-save-button"
                  id="btnSaveEditDailog"
                  disabled={isLoading}
                  color={
                    props.colorSaveButton ? props.colorSaveButton : 'primary'
                  }
                  className={
                    props.classSaveButton
                      ? props.classSaveButton
                      : 'ml-2 acao-step8'
                  }
                  onClick={() =>
                    handleSaveBeforeSave(
                      props.close !== undefined
                        ? props.close
                        : !props.showSaveClose
                    )
                  }
                >
                  {props.enviarComunicado ? (
                    <FormattedMessage id="label.enviar" />
                  ) : (
                    <FormattedMessage
                      id={props.saveLabelId}
                      values={props.saveLabelValues}
                    />
                  )}
                </Button>
              )}
            {!props.disabledEdit &&
              (props.forceShowSave ||
                (props.showActions && props.showSaveClose && allowEdit)) && (
                <Button
                  data-testid="edit-dialog-save-close-button"
                  id="btnSaveEditDailog"
                  disabled={isLoading}
                  color={props.colorSaveCloseButton || props.colorSaveButton}
                  className={
                    props.classSaveCloseButton
                      ? props.classSaveCloseButton
                      : 'ml-2'
                  }
                  onClick={() => handleSaveBeforeSave(true)}
                >
                  <FormattedMessage id="label.salvarEFechar" />
                </Button>
              )}
            {props.showIconDelete && id && props.actions}
            {props.renderRightButtonActions &&
              props.renderRightButtonActions(
                model,
                isNew,
                form.isValid(),
                () => form.showAllErrors(),
                () => handleClose(true),
                handleSaveBeforeSave,
                isLoading
              )}
          </div>
          <div className="botoes-customizados">
            {props.renderButtonActions &&
              props.renderButtonActions(
                model,
                isNew,
                form.isValid(),
                () => form.showAllErrors(),
                () => handleClose(true),
                handleSaveBeforeSave,
                isLoading
              )}
          </div>
        </div>,
      ]
    : null;

  const { formComponent: FormComponent, title, titleField, ...other } = props;
  const titleFieldValue = titleField
    ? form.getModel().getModel(titleField).value
    : null;
  const dialogTitle =
    title && titleFieldValue
      ? `${title} - ${titleFieldValue}`
      : titleFieldValue || title;
  return (
    <Dialog
      width={600}
      {...other}
      padContent
      ref={dialogRef}
      title={dialogTitle}
      titleField={titleFieldValue || ''}
      footer={footerButton}
      actions={editActions}
      onRequestClose={confirmClose}
    >
      <LoadingContainer
        isLoading={isLoading}
        className={props.loadingContainerClassName}
      >
        {props.handleAssistenteVirtualClick && (
          <Row>
            <Col md={12} className="d-flex justify-content-end">
              <Tooltip
                text={props.intl.formatMessage(
                  { id: props.resourceSophieToolTip },
                  { risco: props.resources.risco }
                )}
                placement="bottom"
                maxWidth="25vw"
              >
                <Button
                  autoFocus={false}
                  key={1}
                  className="ml-2"
                  onClick={props.handleAssistenteVirtualClick}
                  type="primary"
                >
                  <SophieIcon color="#fff" size={15} />
                  <span
                    className="ml-2"
                    style={{ marginTop: '2px', marginBottom: '2px' }}
                  >
                    {props.intl.formatMessage({ id: 'gerarInsights' })}
                  </span>
                </Button>
              </Tooltip>
            </Col>
          </Row>
        )}
        {form.getModel().value &&
          HeaderCustomComponent &&
          props.item &&
          props.item.id > 0 && (
            <Row>
              <Col md={12}>
                {React.cloneElement(HeaderCustomComponent, {
                  model: form.getModel(),
                })}
              </Col>
            </Row>
          )}
        <FormComponent
          ref={props.formComponentRef}
          {...other}
          model={form.getModel()}
          resources={props.resources}
          handleCloseEdit={handleClose}
          checkRequiredCustom={checkRequiredCustom}
          showErrorRequired={showErrorRequired}
          setLoading={setIsLoading}
          salvando={isLoading}
        />
      </LoadingContainer>
    </Dialog>
  );
}

export default EditModal;
